import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Landmark, Eye, Banknote, BadgeCheck, RefreshCcw, AlertTriangle, Eraser } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner } from "../../components/common";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { api, errMsg, fmtINR, fmtDate } from "../../lib/api";
import { pokeLiveCollections } from "../../lib/liveCollections";
import { useLiveCollections } from "../../lib/liveCollections";
import { StaffLabel } from "../../components/StaffAvatar";

const STATUS_META = {
  collected: { label: "Collected", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  deposited: { label: "Deposited at bank", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  credited: { label: "Credited", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  bounced: { label: "Bounced", cls: "bg-red-100 text-red-800 border-red-200" },
};

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || { label: status, cls: "" };
  return <Badge variant="outline" className={`text-[11px] ${m.cls}`} data-testid={`cheque-status-${status}`}>{m.label}</Badge>;
};

export default function ChequeDeposits() {
  const { code } = useParams();
  const [tab, setTab] = useState("all");
  const [data, setData] = useState(null);
  const [viewing, setViewing] = useState(null);   // full cheque doc (with photo)
  const [depositFor, setDepositFor] = useState(null);
  const [creditFor, setCreditFor] = useState(null);
  const [depositForm, setDepositForm] = useState({ bank_name: "", branch_address: "", deposit_date: "" });
  const [creditForm, setCreditForm] = useState({ utr_number: "", credit_date: "" });
  const [bounceFor, setBounceFor] = useState(null);
  const [bounceForm, setBounceForm] = useState({ reason: "", bounce_date: "" });
  const [confirmClearBounced, setConfirmClearBounced] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/t/${code}/cheques`, { params: { status: tab } })
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(errMsg(e)); setData({ items: [], counts: {} }); });
  }, [code, tab]);
  useEffect(() => { setData(null); load(); }, [load]);
  useLiveCollections(code, () => { load(); });   // Phase 149: cheques collected / bounced elsewhere appear here

  const openView = async (item) => {
    try {
      const r = await api.get(`/t/${code}/cheques/${item.id}`);
      setViewing(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitDeposit = async () => {
    if (!depositForm.bank_name.trim()) return toast.error("Enter the bank name");
    if (!depositForm.deposit_date) return toast.error("Select the date of submission");
    setBusy(true);
    try {
      await api.put(`/t/${code}/cheques/${depositFor.id}/deposit`, depositForm);
      pokeLiveCollections();   // Phase 149
      toast.success("Cheque marked as deposited at bank");
      setDepositFor(null);
      setDepositForm({ bank_name: "", branch_address: "", deposit_date: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submitCredit = async () => {
    if (!creditForm.utr_number.trim()) return toast.error("Enter the UTR number");
    if (!creditForm.credit_date) return toast.error("Select the credit date");
    setBusy(true);
    try {
      await api.put(`/t/${code}/cheques/${creditFor.id}/credit`, creditForm);
      pokeLiveCollections();   // Phase 149
      toast.success("Cheque marked as credited — payment updated with UTR");
      setCreditFor(null);
      setCreditForm({ utr_number: "", credit_date: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submitBounce = async () => {
    if (!bounceForm.bounce_date) return toast.error("Select the bounce / return date");
    setBusy(true);
    try {
      const r = await api.put(`/t/${code}/cheques/${bounceFor.id}/bounce`, bounceForm);
      pokeLiveCollections();   // Phase 149
      toast.success(`Cheque marked bounced — ${fmtINR(r.data.restored_amount)} restored to dues, receipt cancelled, owner notified`);
      setBounceFor(null);
      setBounceForm({ reason: "", bounce_date: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const counts = data?.counts || {};
  const totalOf = (s) => counts[s]?.count || 0;

  const clearBounced = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/t/${code}/cheques/bounced`);
      pokeLiveCollections();   // Phase 149
      toast.success(`${r.data.deleted} bounced cheque record${r.data.deleted === 1 ? "" : "s"} (${fmtINR(r.data.amount)}) cleared. Payment history and receipts were not affected.`);
      setConfirmClearBounced(false);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Cheque Deposits" subtitle="Track cheques collected in the field — deposit them at the bank and record the credit with UTR">
        <Button variant="outline" size="sm" onClick={load} data-testid="cheques-refresh-button"><RefreshCcw className="h-4 w-4 mr-1.5" /> Refresh</Button>
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {["collected", "deposited", "credited", "bounced"].map((s) => (
          <Card key={s} className="shadow-sm" data-testid={`cheque-kpi-${s}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${s === "bounced" ? "bg-red-100 text-red-700" : "bg-primary/10 text-primary"}`}>
                {s === "collected" ? <Banknote className="h-4.5 w-4.5" /> : s === "deposited" ? <Landmark className="h-4.5 w-4.5" /> : s === "credited" ? <BadgeCheck className="h-4.5 w-4.5" /> : <AlertTriangle className="h-4.5 w-4.5" />}
              </span>
              <div>
                <p className="text-lg font-semibold tabular-nums leading-tight">{totalOf(s)} <span className="text-xs font-normal text-muted-foreground">({fmtINR(counts[s]?.amount || 0)})</span></p>
                <p className="text-xs text-muted-foreground">{STATUS_META[s].label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="all" data-testid="cheque-tab-all">All</TabsTrigger>
            <TabsTrigger value="collected" data-testid="cheque-tab-collected">To deposit</TabsTrigger>
            <TabsTrigger value="deposited" data-testid="cheque-tab-deposited">At bank</TabsTrigger>
            <TabsTrigger value="credited" data-testid="cheque-tab-credited">Credited</TabsTrigger>
            <TabsTrigger value="bounced" data-testid="cheque-tab-bounced">Bounced</TabsTrigger>
          </TabsList>
        </Tabs>
        {totalOf("bounced") > 0 && (
          <Button variant="outline" size="sm"
            className="text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
            onClick={() => setConfirmClearBounced(true)} data-testid="clear-bounced-cheques-button">
            <Eraser className="h-3.5 w-3.5 mr-1.5" /> Clear bounced records
          </Button>
        )}
      </div>

      {!data ? <Spinner /> : data.items.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="cheques-empty-state">
            No cheques in this bucket yet. Cheque payments recorded by collectors will appear here.
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-sm">
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Cheque no</TableHead>
                  <TableHead className="text-xs">Issuing bank</TableHead>
                  <TableHead className="text-xs text-right">Amount</TableHead>
                  <TableHead className="text-xs">Property</TableHead>
                  <TableHead className="text-xs">Collected</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((c) => (
                  <TableRow key={c.id} data-testid="cheque-row">
                    <TableCell className="text-xs font-mono">{c.cheque_number}</TableCell>
                    <TableCell className="text-xs">{c.issuing_bank || "—"}</TableCell>
                    <TableCell className="text-xs text-right font-semibold tabular-nums">{fmtINR(c.amount)}</TableCell>
                    <TableCell className="text-xs">
                      <span className="font-mono">{c.pin}</span>
                      <span className="block text-muted-foreground">{c.owner_name}</span>
                    </TableCell>
                    <TableCell className="text-xs">
                      {fmtDate(c.collection_date)}
                      <span className="block text-muted-foreground"><StaffLabel code={code} userId={c.collected_by} name={c.collected_by_name} empCode={c.collected_by_employee_id} /></span>
                    </TableCell>
                    <TableCell><StatusPill status={c.status} /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => openView(c)} data-testid={`cheque-view-${c.cheque_number}`}>
                        <Eye className="h-3.5 w-3.5 mr-1" /> View
                      </Button>
                      {c.status === "collected" && (
                        <Button variant="outline" size="sm" className="h-8 px-2 ml-1"
                          onClick={() => { setDepositFor(c); setDepositForm({ bank_name: "", branch_address: "", deposit_date: new Date().toISOString().slice(0, 10) }); }}
                          data-testid={`cheque-deposit-${c.cheque_number}`}>
                          <Landmark className="h-3.5 w-3.5 mr-1" /> Mark deposited
                        </Button>
                      )}
                      {c.status === "deposited" && (
                        <Button size="sm" className="h-8 px-2 ml-1"
                          onClick={() => { setCreditFor(c); setCreditForm({ utr_number: "", credit_date: new Date().toISOString().slice(0, 10) }); }}
                          data-testid={`cheque-credit-${c.cheque_number}`}>
                          <BadgeCheck className="h-3.5 w-3.5 mr-1" /> Mark credited
                        </Button>
                      )}
                      {(c.status === "collected" || c.status === "deposited") && (
                        <Button variant="outline" size="sm" className="h-8 px-2 ml-1 text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800"
                          onClick={() => { setBounceFor(c); setBounceForm({ reason: "", bounce_date: new Date().toISOString().slice(0, 10) }); }}
                          data-testid={`cheque-bounce-${c.cheque_number}`}>
                          <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Bounced
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ---------- View cheque ---------- */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Cheque {viewing?.cheque_number}</DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-3 text-sm" data-testid="cheque-detail-dialog">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                <p className="text-muted-foreground">Issuing bank</p><p className="font-medium">{viewing.issuing_bank || "—"}</p>
                <p className="text-muted-foreground">Amount</p><p className="font-semibold tabular-nums">{fmtINR(viewing.amount)}</p>
                <p className="text-muted-foreground">Date on cheque</p><p>{viewing.cheque_date || "—"}</p>
                <p className="text-muted-foreground">Collected on</p><p>{fmtDate(viewing.collection_date)} by <StaffLabel code={code} userId={viewing.collected_by} name={viewing.collected_by_name} empCode={viewing.collected_by_employee_id} /></p>
                <p className="text-muted-foreground">Property</p><p><span className="font-mono">{viewing.pin}</span> • {viewing.owner_name}</p>
                <p className="text-muted-foreground">Receipt</p><p className="font-mono">{viewing.receipt_number}</p>
                <p className="text-muted-foreground">Status</p><p><StatusPill status={viewing.status} /></p>
              </div>
              {viewing.deposit && (
                <div className="rounded-lg border px-3 py-2 text-xs space-y-0.5 bg-sky-50/60" data-testid="cheque-deposit-info">
                  <p className="font-medium text-sm">Deposited at bank</p>
                  <p>{viewing.deposit.bank_name}{viewing.deposit.branch_address ? ` — ${viewing.deposit.branch_address}` : ""}</p>
                  <p>Submitted on {viewing.deposit.deposit_date} by <StaffLabel code={code} userId={viewing.deposit.by} name={viewing.deposit.by_name} /></p>
                </div>
              )}
              {viewing.credit && (
                <div className="rounded-lg border px-3 py-2 text-xs space-y-0.5 bg-emerald-50/60" data-testid="cheque-credit-info">
                  <p className="font-medium text-sm">Amount credited</p>
                  <p>UTR: <span className="font-mono">{viewing.credit.utr_number}</span></p>
                  <p>Credited on {viewing.credit.credit_date} • updated by <StaffLabel code={code} userId={viewing.credit.by} name={viewing.credit.by_name} /></p>
                </div>
              )}
              {viewing.bounce && (
                <div className="rounded-lg border border-red-200 px-3 py-2 text-xs space-y-0.5 bg-red-50/60" data-testid="cheque-bounce-info">
                  <p className="font-medium text-sm text-red-800">Cheque bounced</p>
                  <p>Returned unpaid on {viewing.bounce.bounce_date}{viewing.bounce.reason ? ` — ${viewing.bounce.reason}` : ""}</p>
                  <p>Recorded by <StaffLabel code={code} userId={viewing.bounce.by} name={viewing.bounce.by_name} /> • receipt cancelled and dues restored</p>
                </div>
              )}
              {viewing.photo_base64 ? (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Cheque photocopy</p>
                  <img src={viewing.photo_base64} alt="Cheque photocopy" className="w-full max-h-64 object-contain rounded-md border bg-white" data-testid="cheque-photo-image" />
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No photocopy was uploaded for this cheque.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- Mark deposited ---------- */}
      <Dialog open={!!depositFor} onOpenChange={(o) => !o && setDepositFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Deposit cheque {depositFor?.cheque_number} at bank</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Bank name *</Label>
              <Input value={depositForm.bank_name} onChange={(e) => setDepositForm({ ...depositForm, bank_name: e.target.value })}
                placeholder="Bank where the cheque is submitted" data-testid="deposit-bank-input" />
            </div>
            <div className="space-y-1">
              <Label>Branch address</Label>
              <Input value={depositForm.branch_address} onChange={(e) => setDepositForm({ ...depositForm, branch_address: e.target.value })}
                placeholder="Branch name / address" data-testid="deposit-branch-input" />
            </div>
            <div className="space-y-1">
              <Label>Date of submission *</Label>
              <Input type="date" value={depositForm.deposit_date} onChange={(e) => setDepositForm({ ...depositForm, deposit_date: e.target.value })}
                data-testid="deposit-date-input" />
            </div>
            <Button className="w-full" onClick={submitDeposit} disabled={busy} data-testid="deposit-submit-button">
              {busy ? "Saving..." : "Mark as deposited"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ---------- Mark credited ---------- */}
      <Dialog open={!!creditFor} onOpenChange={(o) => !o && setCreditFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Record credit for cheque {creditFor?.cheque_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>UTR number *</Label>
              <Input value={creditForm.utr_number} onChange={(e) => setCreditForm({ ...creditForm, utr_number: e.target.value })}
                placeholder="UTR of the credit in the municipal account" data-testid="credit-utr-input" />
            </div>
            <div className="space-y-1">
              <Label>Credit date *</Label>
              <Input type="date" value={creditForm.credit_date} onChange={(e) => setCreditForm({ ...creditForm, credit_date: e.target.value })}
                data-testid="credit-date-input" />
            </div>
            <p className="text-[11px] text-muted-foreground">The UTR is also stamped on the original payment record for reconciliation.</p>
            <Button className="w-full" onClick={submitCredit} disabled={busy} data-testid="credit-submit-button">
              {busy ? "Saving..." : "Mark as credited"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* ---------- Mark bounced ---------- */}
      <Dialog open={!!bounceFor} onOpenChange={(o) => !o && setBounceFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-red-800">Mark cheque {bounceFor?.cheque_number} as bounced</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-red-200 bg-red-50/60 px-3 py-2 text-xs text-red-800" data-testid="bounce-warning">
              This reverses the payment of {fmtINR(bounceFor?.amount || 0)}: the receipt is cancelled, the amount is restored to the property's outstanding dues, and the owner is notified by SMS / WhatsApp.
            </div>
            <div className="space-y-1">
              <Label>Bounce / return date *</Label>
              <Input type="date" value={bounceForm.bounce_date} onChange={(e) => setBounceForm({ ...bounceForm, bounce_date: e.target.value })}
                data-testid="bounce-date-input" />
            </div>
            <div className="space-y-1">
              <Label>Reason (from bank memo)</Label>
              <Input value={bounceForm.reason} onChange={(e) => setBounceForm({ ...bounceForm, reason: e.target.value })}
                placeholder="e.g. Insufficient funds / signature mismatch" data-testid="bounce-reason-input" />
            </div>
            <Button variant="destructive" className="w-full" onClick={submitBounce} disabled={busy} data-testid="bounce-submit-button">
              {busy ? "Reversing..." : "Confirm bounce & restore dues"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ---------- Clear bounced records (test-data cleanup) ---------- */}
      <AlertDialog open={confirmClearBounced} onOpenChange={(o) => !o && setConfirmClearBounced(false)}>
        <AlertDialogContent data-testid="clear-bounced-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all {totalOf("bounced")} bounced cheque record{totalOf("bounced") === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  This permanently removes the bounced entries ({fmtINR(counts.bounced?.amount || 0)}) from the
                  cheque tracker. Use this to wipe test records before going live.
                </p>
                <p className="font-medium text-foreground">
                  The reversed payments, cancelled receipts and restored property dues are NOT affected.
                </p>
                <p>This action is recorded in the audit log and cannot be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-bounced-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={clearBounced} disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="clear-bounced-confirm-button">
              {busy ? "Clearing..." : "Clear bounced records"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
